import {Component, Input, OnDestroy, OnInit} from "@angular/core";
import {DialogComponent} from '../../../../../ng2-bootstrap-modal/dialog.component';
import {DialogService} from '../../../../../ng2-bootstrap-modal/dialog.service';
import {NavigationEnd, Router} from "@angular/router";
import * as fromStore from "../../../../store";
import {distinctUntilChanged, filter, takeUntil} from "rxjs/operators";
import {
  UserSubscription,
  UserSubscriptionCategory,
  UserSubscriptionIds,
  UserSubscriptionType
} from "../../../../user/user-subscription";
import {Store} from "@ngrx/store";
import {PaymentDialogComponent} from "../../../../workspace/billing/payment-dialog/payment-dialog.component";
import {User} from "../../../../user/user";
import {GoogleAnalyticsService} from "../../../services/google-analytics/google-analytics.service";
import {Utils} from "../../../utils/utils";
import {animate, style, transition, trigger} from "@angular/animations";
// tslint:disable-next-line:import-blacklist
import {combineLatest, Subject} from "rxjs";
import {RouteDataService} from "../../../services/route-data/route-data.service";
import {DbModalService} from "../../../../modules/modal/modal";
import {Config} from "../../../../config/config";
import {Website} from "../../../../websites/website/website";
import {CreateWebsiteHelpersService} from "../../../../services/local/create-website-helpers.service";
import {OptionRegion} from "../../../models/option-region.model";
import {UserService} from "../../../../user/user.service";
import {DbActiveModal} from "../../../../modules/modal/modal-ref";

@Component({
    selector: 'db-account-upgraded-dialog',
    templateUrl: './account-upgraded-dialog.component.html',
    styleUrls: ['./account-upgraded-dialog.component.scss'],
    animations: [
        trigger('slide', [
            transition(':enter', [
                style({ transform: 'translateX(20px)', opacity: 0 }),
                animate('200ms ease-out', style({ transform: 'translateX(0)', opacity: 1 }))
            ]),
            transition(':leave', [
                style({ transform: 'translateX(0)', opacity: 1, position: 'absolute' }),
                animate('200ms ease-in', style({ transform: 'translateX(-60px)', opacity: 0, position: 'absolute' }))
            ])
        ]),
        trigger('scale', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                style({ opacity: 1, position: 'absolute', right: 0 }),
                animate('200ms ease-in', style({ opacity: 0, position: 'absolute', right: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class AccountUpgradedDialogComponent extends DialogComponent<null, Boolean> implements OnInit, OnDestroy {

  @Input() extend: boolean = false;
  @Input() extendNotEnough: boolean = false;
  @Input() currentSubscription?: UserSubscription;
  @Input() upgradedFromTrial?: boolean;
  @Input() upgradedFromBooster?: boolean;
  @Input() fromPaidDialog?: boolean;
  @Input() showSetupFlow?: boolean;
  @Input() isDemoUser?: boolean;
  public forceImport: boolean;
  public user: User;
  public loading: boolean = true;
  public UserSubscriptionCategory = UserSubscriptionCategory;
  public UserSubscriptionIds = UserSubscriptionIds;
  public upgradeablePremiumSubscription: UserSubscription;
  public upgradeableEnterpriseSubscription: UserSubscription;
  public UserSubscriptionType = UserSubscriptionType;
  public platformSubscription: boolean = false;
  public step = 1;
  public pageId: string;
  public demoUser: boolean;
  public websites: Website[];
  public isAvailableEcommerceFeatures: boolean = false;
  public userLocalStorage: any;
  public regions: OptionRegion[] = [];
  public websiteData: any;
  isMobile = window.innerWidth <= 768;
  currentStep = 0;
  websiteId: number;
  websiteUrl: string;
  tempalteType: string;
  showSetupFlowVPS = false;
  campaignInfo: any;

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  private _payments: any;
  private _hasSentUpgradeEvent = false;

  constructor(
    private _router: Router,
    private _store: Store<fromStore.DbStoreState>,
    private _dialogService: DialogService,
    private _activeModal: DbActiveModal,
    private _modalService: DbModalService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _routeDataService: RouteDataService,
    private _userService: UserService,
    private readonly _createWebsiteHelpersService: CreateWebsiteHelpersService,
  ) {
    super(
      _dialogService
    );
  }


  ngOnInit(): void {
    this.campaignInfo = this._userService.getCampaignInfo();
    this.isMobile = window.innerWidth <= 768;
    this.showSetupFlowVPS && (this.step = 2);
    if ((!!this.isDemoUser || !!this.showSetupFlow) && this.step !== 4) {
      this.step = 3;
      this.forceImport = true;
      this.platformSubscription = true;
      this.loading = false;
    }
    this._getLocalUserData();
    this._getUserData();

    if (this.currentSubscription?.isDedicatedVM) {
      const vpsRegion = {
        label: this.currentSubscription?.plan?.dedicatedVMInfo?.zoneName,
        value: this.currentSubscription?.plan?.dedicatedVMInfo?.vmZone,
        isAvailable: true
      };
      this.regions = [vpsRegion];
    } else {
      this._createWebsiteHelpersService.getRegions(null).then(regions => {
        this.regions = regions;
      });
    }
    this.pageId = this._routeDataService.getCurrentRouteData().pageId;
    this._router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.pageId = this._router && this._router.url.split("/").pop();
      });
  }

  private _getUserData(): void {
    combineLatest([
      this._store.select(fromStore.getUserLoaded).pipe(filter(loaded => loaded)),
      this._store.select(fromStore.getUser),
      this._store.select(fromStore.getUserSubscription)
    ])
      .pipe(
        // Compare old vs new subscription by ID (or whatever uniquely identifies it)
        distinctUntilChanged(
          ([, , prevSub], [, , currSub]) => prevSub.planId === currSub.planId
        ),
        takeUntil(this._destroyStream$),
      )
      .subscribe(([loaded, user, subscription]) => {
        this.user = user;
        this.currentSubscription = subscription;

        // Evaluate demoUser and other flags
        this.demoUser = !!(
          user?.signUpFor?.LastChosenOption?.answer &&
          ['ai-demo-section', 'ai-demo-section-after-signup', 'ai-demo-template', 'ai-demo-template-after-signup'].includes(
            user.signUpFor.LastChosenOption.answer
          )
        ) && Utils.isTruthy(this.upgradedFromTrial);

        this._handleUpgradeAnalytics(subscription);

        this.updatePlatformSubscriptionFlags(subscription);

        this._fetchAsyncData();
      });
  }

  private _handleUpgradeAnalytics(subscription: UserSubscription): void {
    if (!subscription) {
      return;
    }
    const subscriptionTitle =
      subscription?.subscriptionOriginalTitle ||
      subscription?.subscriptionTitleName;

    if (
      !this._hasSentUpgradeEvent &&
      !this.showSetupFlow &&
      !this.currentSubscription?.trialClient
    ) {
      this._hasSentUpgradeEvent = true;
    }
  }

  private _fetchAsyncData(): void {
    this._store.select(fromStore.getPayments).subscribe(userPayments => {
      this._payments = userPayments;
      if (((!!this.demoUser && userPayments && !userPayments.length) || this.showSetupFlow || this.showSetupFlowVPS) && this.step !== 4) {
        this.step = 3;
        this.forceImport = true;
      }
    });
    // Fetch non-critical data asynchronously

    this._store.select(fromStore.getUserAvailableHostingSubscriptions()).subscribe(subscriptions => {
      this.updateUpgradeableSubscriptions(subscriptions);
    });

    this._store.select(fromStore.getWebsites).subscribe(websites => {
      this.websites = websites;
      this.handleWebsites(websites);
    });

    this.loading = false;
  }

  private _getLocalUserData() {
    this._store.select(fromStore.getUserLocalStorage)
      .pipe(takeUntil(this._destroyStream$))
      .subscribe(localStorage => {
        this.userLocalStorage = localStorage;
      });
  }


  // Helper method to update platform subscription flags
  updatePlatformSubscriptionFlags(currentSubscription) {
    if (!currentSubscription) return;

    this.platformSubscription = currentSubscription?.category === UserSubscriptionCategory?.PERSONAL
      || currentSubscription?.category === UserSubscriptionCategory?.PREMIUM
      || currentSubscription?.category === UserSubscriptionCategory?.ENTERPRISE
      || currentSubscription?.category === UserSubscriptionCategory?.ENTERPRISE_ULTIMATE
      || currentSubscription?.category === UserSubscriptionCategory?.PROFESSIONAl;
    if (this.currentSubscription?.isEcommerce || this.currentSubscription?.isAgency
      || this.currentSubscription?.isBusiness) {
      this.isAvailableEcommerceFeatures = true;
    }
  }

  // Helper method to update upgradeable subscriptions
  updateUpgradeableSubscriptions(availableHostingSubscriptions) {
    if (!this.currentSubscription || !availableHostingSubscriptions) return;

    if (this.currentSubscription && this.currentSubscription?.category !== UserSubscriptionCategory?.NONE && [UserSubscriptionCategory?.PREMIUM, UserSubscriptionCategory?.ENTERPRISE].includes(this.currentSubscription?.category)) {
      this.upgradeablePremiumSubscription = null;
    } else {
      this.upgradeablePremiumSubscription = availableHostingSubscriptions.find((userSubscription: UserSubscription): boolean => {
        return userSubscription && userSubscription?.plan && userSubscription?.category === UserSubscriptionCategory?.PREMIUM && this.currentSubscription && userSubscription?.plan?.periodNumber === this.currentSubscription?.plan?.periodNumber;
      });
    }

    if (this.currentSubscription && this.currentSubscription?.category !== UserSubscriptionCategory?.NONE && [UserSubscriptionCategory?.ENTERPRISE].includes(this.currentSubscription?.category)) {
      this.upgradeableEnterpriseSubscription = null;
    } else {
      this.upgradeableEnterpriseSubscription = availableHostingSubscriptions.find((userSubscription: UserSubscription): boolean => {
        return userSubscription && userSubscription?.category === UserSubscriptionCategory?.ENTERPRISE && this.currentSubscription && userSubscription?.plan?.periodNumber === this.currentSubscription?.plan?.periodNumber;
      });
    }
  }

  // Helper method to handle websites
  handleWebsites(websites: any[]) {
    if (websites.length === 1 && this.currentSubscription?.category === UserSubscriptionCategory?.PERSONAL) {
      this._store.dispatch(new fromStore.LoadInfo(websites[0].id));
    }
  }


  override ngOnDestroy() {
    this.userLocalStorage && this._userService.setToLocal({...this.userLocalStorage, ngDestroy: 'called'});
    this._store.dispatch(new fromStore.LoadUserAvailableSubscriptions());
    super.ngOnDestroy();
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _createWebsite(): void {
    localStorage.removeItem('dbForVPSUpgrade');
    this.userLocalStorage && this._userService.setToLocal({
      ...this.userLocalStorage,
      _createWebsite: 'called',
      demoUser: this.demoUser,
      websites: this.websites,
      websiteLength: this.websites && this.websites.length
    });
    if (!!this.demoUser || (Object.keys(this.websiteData).length > 0 && this.isAvailableEcommerceFeatures)) {
      if (this.websites && this.websites.length === 1 && this.websites[0] && this.websites[0].id && this.websites[0].id !== Config.demoDomainId) return;
      this._store.dispatch(new fromStore.LoadWebsites());
      this.step = 3;
    }
    else {
      this._closeAllDialogs();
    }
  }

  public showSuccessStep(data): void {
    this.tempalteType = data.templateType;
    data.websiteId && (this.websiteId = data.websiteId);
    this.step = 4;
  }

  public override close(fromSlackStep = false, $event = {}): void {
    if (fromSlackStep) {
      const analyticsData = {
        eventCategory: 'Dashboard Action',
        eventAction: 'Slack button Click',
        eventLabel: 'Popup'
      };
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    }
    if (((!!this.demoUser && this._payments && !this._payments.length) || this.showSetupFlow) && this.step !== 4) {
      this.step = 2;
    }
    if (this.step === 4 && this.isMobile) {
      this.redirectToWebsite();
      this._closeAllDialogs();
    }
    this.websiteData = $event;
    if (Object.keys(this.websiteData).length > 0 || !this.isAvailableEcommerceFeatures || (this._payments && this._payments.length) || !this.demoUser) {
      const agencyInUpgradePlanPage = this.currentSubscription && this.currentSubscription.category === UserSubscriptionCategory.ENTERPRISE && this.pageId === 'upgrade-plan';
      if (agencyInUpgradePlanPage || (!this.extendNotEnough && !this.upgradedFromTrial && !this.fromPaidDialog)) {
        this._router.navigateByUrl('/websites');
      }
      if (!(this.websites && this.websites.length === 1 && this.websites[0] && this.websites[0].id && this.websites[0].id === Config.demoDomainId)) {
        this._closeAllDialogs();
      }
      this._createWebsite();
    }
  }

  redirectToWebsite() {
    this._router.navigate(['/websites']);
  }

  public _closeAllDialogs(): void {
    this._dialogService.removeAll();
    this._modalService.dismissAll();
    super.close();
  }

  websiteUrlUpdate($event: string) {
    this.websiteUrl = $event;
  }
}
